import { useState } from 'react';
import XButton from 'src/components/Buttons/XButton';
import { cm } from 'src/utils/classNames';
import { WithChildren } from 'src/utils/UtilityTypes';

interface BaseProps extends WithChildren {
	onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface NonCustomVariantProps extends BaseProps {
	/**
	 * Note, when using links with the `info` variant, be sure to use a different
	 * color than `purple-link`, as the color contrast isn't sufficient.
	 */
	variant: 'announcement' | 'info' | 'warning' | 'error';
	barClassName?: never;
	closeButtonClassName?: never;
}

interface CustomVariantProps extends BaseProps {
	variant: 'custom';
	barClassName: string;
	closeButtonClassName: string;
}

type Props = NonCustomVariantProps | CustomVariantProps;

export default function StatusBar({
	variant,
	barClassName: barClassNameFromProps,
	closeButtonClassName: closeButtonClassNameFromProps,
	children,
	onClose,
}: Props) {
	const [isVisible, setIsVisible] = useState(true);

	const barClassName = {
		announcement:
			'bg-gradient-to-r from-button-primary-bg via-royal-purple-400 to-dark-purple-50 text-white',
		info: 'bg-text-secondary text-white',
		warning: 'bg-star-yellow-bg text-text-default',
		error: 'bg-negative-red-secondary-bg text-text-negative-red-button',
		custom: '',
	}[variant];

	const closeButtonClassName = {
		// By default, we don't change the close button color on hover.
		announcement: 'text-white hover:text-white',
		info: 'text-white hover:text-white',
		warning: 'text-text-default hover:text-text-default',
		error: 'text-text-default hover:text-text-default',
		custom: '',
	}[variant];

	const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
		setIsVisible(false);

		onClose?.(e);
	};

	if (!isVisible) {
		return null;
	}

	return (
		// Several variant bg colors are opacity-based, so this wrapper is required
		// to provide the necessary base color for them to render on top of.
		<div className="bg-container-bg">
			<div
				className={cm(
					'flex items-start gap-x-sm px-lg py-sm sm:items-center',
					barClassName,
					barClassNameFromProps
				)}
			>
				<span
					// `wrap-anywhere` ensures long strings (e.g authority) don't cause an overflow.
					className="grow pr-sm typo-b4 wrap-anywhere sm:ml-2xl sm:text-center"
				>
					{children}
				</span>

				<XButton
					size="sm"
					variant="tertiary"
					className={cm(closeButtonClassName, closeButtonClassNameFromProps)}
					onClick={handleClose}
				/>
			</div>
		</div>
	);
}
